import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../../hooks/useApi';
import {
  PackageIcon, SearchIcon, AlertIcon, CheckIcon, XIcon,
  RefreshIcon, EyeIcon, ReceiptIcon, PlayIcon, UsersIcon,
  TrendUpIcon, CurrencyIcon,
} from '../../components/Icons';

// ── Utilitários ───────────────────────────────────────────
const fmt = (v) =>
  new Intl.NumberFormat('pt-AO', { maximumFractionDigits: 0 }).format(v || 0) + ' Kz';

const fmtData = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('pt-PT', { day: '2-digit', month: 'short', year: 'numeric' });
};

const ESTADO_MAP = {
  pendente:   { label: 'Pendente',   badgeCls: 'badge-warning', dot: '#D97706' },
  confirmada: { label: 'Confirmada', badgeCls: 'badge-info',    dot: '#2563EB' },
  em_uso:     { label: 'Em uso',     badgeCls: 'badge-orange',  dot: '#F97316' },
  concluida:  { label: 'Concluída',  badgeCls: 'badge-success', dot: '#16A34A' },
  cancelada:  { label: 'Cancelada',  badgeCls: 'badge-danger',  dot: '#DC2626' },
};

const CATEGORIAS_COR = {
  'Moda':        { bg: 'linear-gradient(135deg,#fde8d8,#fed7b0)', ic: '#F97316' },
  'Electrónica': { bg: 'linear-gradient(135deg,#dbeafe,#bfdbfe)', ic: '#2563EB' },
  'Música':      { bg: 'linear-gradient(135deg,#dcfce7,#bbf7d0)', ic: '#16A34A' },
  'Lazer':       { bg: 'linear-gradient(135deg,#fef9c3,#fde68a)', ic: '#D97706' },
  'Eventos':     { bg: 'linear-gradient(135deg,#ede9fe,#ddd6fe)', ic: '#7C3AED' },
  default:       { bg: 'linear-gradient(135deg,#f0f4f8,#dde1e7)', ic: '#64748b' },
};

const corCategoria = (nome) => CATEGORIAS_COR[nome] || CATEGORIAS_COR.default;
const iniciais = (nome) => (nome || '?').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();

const CORES_AVATAR = ['#1A4A8A', '#F97316', '#16A34A', '#D97706', '#7C3AED', '#0E7490', '#E11D48', '#059669'];
const corAvatar = (str) => CORES_AVATAR[(str || '').charCodeAt(0) % CORES_AVATAR.length];

// ── Exportar CSV ──────────────────────────────────────────
function exportarCSV(reservas) {
  const agora = new Date();
  const dataHora = agora.toLocaleDateString('pt-PT') + ' ' + agora.toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' });
  const BOM = '\uFEFF';

  const linhas = [
    ['KIALUGA - PLATAFORMA DE ALUGUER', '', '', '', '', '', '', '', '', '', ''],
    ['Relatório de Transações', '', '', '', '', '', '', '', '', '', ''],
    [`Exportado em: ${dataHora}`, '', '', '', '', '', '', '', '', '', ''],
    [`Total de registos: ${reservas.length}`, '', '', '', '', '', '', '', '', '', ''],
    ['', '', '', '', '', '', '', '', '', '', ''],
    ['RESUMO FINANCEIRO', '', '', '', '', '', '', '', '', '', ''],
    ['GMV Total (Kz)', reservas.reduce((s, r) => s + parseFloat(r.valor_aluguer || 0), 0).toFixed(2), '', '', '', '', '', '', '', '', ''],
    ['Cauções em Escrow (Kz)', reservas.filter(r => ['confirmada', 'em_uso'].includes(r.estado)).reduce((s, r) => s + parseFloat(r.valor_caucao || 0), 0).toFixed(2), '', '', '', '', '', '', '', '', ''],
    ['', '', '', '', '', '', '', '', '', '', ''],
    ['─────────────────────────────────────────────────────────────────────────────', '', '', '', '', '', '', '', '', '', ''],
    ['ID Reserva', 'Produto / Serviço', 'Categoria', 'Cliente', 'Fornecedor', 'Data Início', 'Data Fim', 'Dias', 'Valor Aluguer (Kz)', 'Caução (Kz)', 'Estado'],
    ['─────────────────────────────────────────────────────────────────────────────', '', '', '', '', '', '', '', '', '', ''],
    ...reservas.map(r => [
      r.id,
      r.anuncio_titulo || '—',
      r.categoria_nome || '—',
      r.cliente_nome   || '—',
      r.fornecedor_nome|| '—',
      fmtData(r.data_inicio),
      fmtData(r.data_fim),
      r.total_dias     || 1,
      parseFloat(r.valor_aluguer || 0).toFixed(2),
      parseFloat(r.valor_caucao  || 0).toFixed(2),
      ESTADO_MAP[r.estado]?.label || r.estado,
    ]),
    ['', '', '', '', '', '', '', '', '', '', ''],
    ['─────────────────────────────────────────────────────────────────────────────', '', '', '', '', '', '', '', '', '', ''],
    ['KiAluga © ' + agora.getFullYear() + ' — Todos os direitos reservados', '', '', '', '', '', '', '', '', '', ''],
    ['www.kialuga.ao', '', '', '', '', '', '', '', '', '', ''],
  ];

  const csv = BOM + linhas.map(row =>
    row.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')
  ).join('\r\n');

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = `kialuga_transacoes_${agora.toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ── Componente principal ──────────────────────────────────
export default function TransacoesAdmin() {
  const navigate = useNavigate();

  const [reservas,   setReservas]   = useState([]);
  const [contagens,  setContagens]  = useState({});
  const [gmv,        setGmv]        = useState(0);
  const [caucoes,    setCaucoes]    = useState(0);
  const [carregando, setCarregando] = useState(true);
  const [erro,       setErro]       = useState(null);
  const [activeTab,  setActiveTab]  = useState('todas');
  const [search,     setSearch]     = useState('');
  const [expandido,  setExpandido]  = useState(null);
  const [accaoId,    setAccaoId]    = useState(null);

  const carregar = useCallback(async () => {
    setCarregando(true); setErro(null);
    try {
      const params = new URLSearchParams();
      if (activeTab !== 'todas') params.set('estado', activeTab);
      if (search.trim())         params.set('pesquisa', search.trim());
      const d = await apiFetch(`/admin/reservas?${params}`);
      setReservas(d.reservas || []);
      setContagens(d.contagens || {});
      setGmv(d.gmv || 0);
      setCaucoes(d.caucoes || 0);
    } catch (e) {
      setErro(e.message);
    } finally {
      setCarregando(false);
    }
  }, [activeTab, search]);

  useEffect(() => { carregar(); }, [carregar]);

  useEffect(() => {
    const t = setTimeout(() => carregar(), 400);
    return () => clearTimeout(t);
  }, [search]); // eslint-disable-line

  const actualizarEstado = async (id, estado, label) => {
    if (!window.confirm(`Confirmar: marcar reserva como "${label}"?`)) return;
    setAccaoId(id);
    try {
      await apiFetch(`/admin/reservas/${id}/estado`, 'PATCH', { estado });
      await carregar();
    } catch (e) {
      alert('Erro: ' + e.message);
    } finally {
      setAccaoId(null);
    }
  };

  const TABS = [
    { key: 'todas',     label: 'Todas' },
    { key: 'pendente',  label: 'Pendentes' },
    { key: 'confirmada',label: 'Confirmadas' },
    { key: 'em_uso',    label: 'Em uso' },
    { key: 'concluida', label: 'Concluídas' },
    { key: 'cancelada', label: 'Canceladas' },
  ];

  const tabCount = (key) => {
    if (key === 'todas') return contagens.total || 0;
    return contagens[key] || 0;
  };

  const disputaCount = (reservas || []).filter(r => r.disputa_id).length;

  return (
    <>
      {/* Shimmer animation */}
      <style>{`@keyframes shimmer{0%{background-position:200% 0}100%{background-position:-200% 0}}`}</style>

      {/* ── Topbar ── */}
      <div className="topbar">
        <div>
          <div className="topbar-title">Gestão de Transações</div>
          <div className="topbar-sub">
            {carregando
              ? 'A carregar…'
              : `${contagens.total || 0} transações · GMV ${fmt(gmv)}`}
          </div>
        </div>
        <div className="topbar-actions">
          <button
            className="btn btn-ghost"
            onClick={carregar}
            title="Actualizar"
            style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
          >
            <RefreshIcon size={14} /> Actualizar
          </button>
          <button
            className="btn btn-outline"
            onClick={() => exportarCSV(reservas)}
            disabled={!reservas.length}
            style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
          >
            <ReceiptIcon size={14} /> Exportar CSV
          </button>
        </div>
      </div>

      <div className="page-content">

        {/* ── Stats cards ── */}
        <div className="stats-grid" style={{ marginBottom: '20px' }}>

          {/* Total */}
          <div className="stat-card" style={{ position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', right: '-12px', bottom: '-12px', width: '80px', height: '80px', borderRadius: '50%', background: 'rgba(249,115,22,0.06)', pointerEvents: 'none' }} />
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '10px' }}>
              <div className="stat-icon" style={{ background: '#FFF7ED' }}>
                <ReceiptIcon size={18} color="#F97316" />
              </div>
            </div>
            <div className="stat-value">{carregando ? '—' : contagens.total || 0}</div>
            <div className="stat-label">Total transações</div>
            <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>Todas as reservas</div>
          </div>

          {/* GMV */}
          <div className="stat-card" style={{ position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', right: '-12px', bottom: '-12px', width: '80px', height: '80px', borderRadius: '50%', background: 'rgba(22,163,74,0.06)', pointerEvents: 'none' }} />
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '10px' }}>
              <div className="stat-icon" style={{ background: '#F0FDF4' }}>
                <TrendUpIcon size={18} color="#16A34A" />
              </div>
            </div>
            <div className="stat-value" style={{ fontSize: '16px' }}>{carregando ? '—' : fmt(gmv)}</div>
            <div className="stat-label">GMV Total</div>
            <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>Volume bruto de mercadoria</div>
          </div>

          {/* Cauções */}
          <div className="stat-card" style={{ position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', right: '-12px', bottom: '-12px', width: '80px', height: '80px', borderRadius: '50%', background: 'rgba(37,99,235,0.06)', pointerEvents: 'none' }} />
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '10px' }}>
              <div className="stat-icon" style={{ background: '#EFF6FF' }}>
                <CurrencyIcon size={18} color="#2563EB" />
              </div>
              {(contagens.confirmada || 0) + (contagens.em_uso || 0) > 0 && (
                <span className="badge badge-info" style={{ fontSize: '10px' }}>
                  {(contagens.confirmada || 0) + (contagens.em_uso || 0)} ativas
                </span>
              )}
            </div>
            <div className="stat-value" style={{ fontSize: '16px' }}>{carregando ? '—' : fmt(caucoes)}</div>
            <div className="stat-label">Cauções em escrow</div>
            <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>Retidas actualmente</div>
          </div>

          {/* Pendentes / Disputas */}
          <div className="stat-card" style={{ position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', right: '-12px', bottom: '-12px', width: '80px', height: '80px', borderRadius: '50%', background: disputaCount > 0 ? 'rgba(220,38,38,0.06)' : 'rgba(217,119,6,0.06)', pointerEvents: 'none' }} />
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '10px' }}>
              <div className="stat-icon" style={{ background: disputaCount > 0 ? '#FEF2F2' : '#FFFBEB' }}>
                {disputaCount > 0
                  ? <AlertIcon size={18} color="#DC2626" />
                  : <UsersIcon size={18} color="#D97706" />}
              </div>
              {disputaCount > 0 && (
                <span className="badge badge-danger" style={{ fontSize: '10px' }}>Atenção</span>
              )}
            </div>
            <div className="stat-value" style={{ color: disputaCount > 0 ? 'var(--danger)' : (contagens.pendente > 0 ? 'var(--warning)' : 'inherit') }}>
              {carregando ? '—' : disputaCount > 0 ? disputaCount : (contagens.pendente || 0)}
            </div>
            <div className="stat-label">{disputaCount > 0 ? 'Disputas activas' : 'Pendentes confirmação'}</div>
            <div style={{ fontSize: '11px', color: disputaCount > 0 ? 'var(--danger)' : 'var(--text-muted)', marginTop: '2px' }}>
              {disputaCount > 0 ? 'Requerem atenção' : 'Aguardam acção'}
            </div>
          </div>

        </div>

        {/* ── Filter tabs + search ── */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px', marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
            {TABS.map(t => (
              <button
                key={t.key}
                onClick={() => setActiveTab(t.key)}
                style={{
                  padding: '6px 14px',
                  borderRadius: '20px',
                  border: activeTab === t.key ? 'none' : '1px solid var(--border)',
                  background: activeTab === t.key ? 'var(--orange)' : 'var(--card)',
                  color: activeTab === t.key ? '#fff' : 'var(--text-secondary)',
                  fontSize: '12px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                  display: 'flex', alignItems: 'center', gap: '5px',
                  fontFamily: 'inherit',
                }}
              >
                {t.label}
                {tabCount(t.key) > 0 && (
                  <span style={{
                    background: activeTab === t.key ? 'rgba(255,255,255,0.25)' : 'var(--surface)',
                    borderRadius: '10px', padding: '1px 7px', fontSize: '10px', fontWeight: '700',
                  }}>
                    {tabCount(t.key)}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Search */}
          <div style={{ position: 'relative', width: '260px' }}>
            <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
              <SearchIcon size={13} color="var(--text-muted)" />
            </span>
            <input
              style={{
                width: '100%', padding: '8px 12px 8px 32px',
                border: '1px solid var(--border)', borderRadius: '20px',
                fontSize: '12px', fontFamily: 'inherit', outline: 'none',
                background: 'var(--card)', color: 'var(--text-primary)',
                transition: 'border-color 0.15s',
                boxSizing: 'border-box',
              }}
              type="text"
              placeholder="Pesquisar…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              onFocus={e => e.target.style.borderColor = 'var(--orange)'}
              onBlur={e => e.target.style.borderColor = 'var(--border)'}
            />
          </div>
        </div>

        {/* ── Table card ── */}
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>

          {/* Error banner */}
          {erro && (
            <div style={{
              padding: '12px 20px',
              background: '#FEF2F2',
              borderBottom: '1px solid #FECACA',
              display: 'flex', alignItems: 'center', gap: '8px',
              color: '#DC2626', fontSize: '13px',
            }}>
              <AlertIcon size={15} />
              {erro} —{' '}
              <button
                className="btn btn-ghost btn-sm"
                onClick={carregar}
                style={{ color: '#DC2626', textDecoration: 'underline', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: '12px' }}
              >
                Tentar novamente
              </button>
            </div>
          )}

          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ width: '90px' }}>ID</th>
                  <th>Produto</th>
                  <th>Cliente</th>
                  <th>Fornecedor</th>
                  <th>Período</th>
                  <th>Aluguer</th>
                  <th>Caução</th>
                  <th>Estado</th>
                  <th style={{ width: '200px' }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {carregando ? (
                  /* ── Skeleton rows ── */
                  [...Array(5)].map((_, i) => (
                    <tr key={i}>
                      {[...Array(9)].map((_, j) => (
                        <td key={j} style={{ padding: '12px' }}>
                          <div style={{
                            height: j === 1 ? '32px' : '14px',
                            width: j === 0 ? '80px' : j === 1 ? '160px' : j === 8 ? '100px' : '80%',
                            borderRadius: '6px',
                            background: 'linear-gradient(90deg, #F1F5F9 25%, #E2E8F0 50%, #F1F5F9 75%)',
                            backgroundSize: '200% 100%',
                            animation: 'shimmer 1.4s infinite',
                          }} />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : reservas.length === 0 ? (
                  /* ── Empty state ── */
                  <tr>
                    <td colSpan={9}>
                      <div style={{ padding: '60px 20px', textAlign: 'center' }}>
                        <svg width="120" height="90" viewBox="0 0 120 90" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <rect x="15" y="20" width="90" height="60" rx="8" fill="#F1F5F9" stroke="#E2E8F0" strokeWidth="1.5"/>
                          <rect x="25" y="32" width="50" height="6" rx="3" fill="#CBD5E1"/>
                          <rect x="25" y="44" width="36" height="4" rx="2" fill="#E2E8F0"/>
                          <rect x="25" y="54" width="44" height="4" rx="2" fill="#E2E8F0"/>
                          <rect x="25" y="64" width="28" height="4" rx="2" fill="#E2E8F0"/>
                          <circle cx="90" cy="30" r="16" fill="#FFF7ED" stroke="#FED7AA" strokeWidth="1.5"/>
                          <path d="M90 24v8M90 34h.01" stroke="#F97316" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                        <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-secondary)', marginTop: '12px' }}>
                          Nenhuma transação encontrada
                        </div>
                        <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>
                          Tenta ajustar os filtros de pesquisa
                        </div>
                      </div>
                    </td>
                  </tr>
                ) : reservas.map(r => {
                  const cor       = corCategoria(r.categoria_nome);
                  const eMap      = ESTADO_MAP[r.estado] || { label: r.estado, badgeCls: 'badge-neutral', dot: '#64748B' };
                  const temDisputa = !!r.disputa_id;
                  const loading    = accaoId === r.id;

                  return (
                    <React.Fragment key={r.id}>
                      <tr style={{
                        borderLeft: temDisputa ? '3px solid var(--danger)' : 'none',
                        background: temDisputa ? 'rgba(220,38,38,0.02)' : undefined,
                      }}>

                        {/* ID */}
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <span style={{ fontFamily: 'monospace', fontSize: '10px', color: 'var(--text-muted)', letterSpacing: '-0.3px' }}>
                              {r.id.slice(0, 8)}…
                            </span>
                            {temDisputa && (
                              <span title="Disputa activa" style={{ color: 'var(--danger)', lineHeight: 0 }}>
                                <AlertIcon size={10} />
                              </span>
                            )}
                          </div>
                        </td>

                        {/* Produto */}
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
                            <div style={{
                              width: '34px', height: '34px', borderRadius: '8px',
                              background: cor.bg, display: 'flex', alignItems: 'center',
                              justifyContent: 'center', flexShrink: 0,
                            }}>
                              <PackageIcon size={15} color={cor.ic} />
                            </div>
                            <div>
                              <div style={{ fontWeight: '600', fontSize: '12px', color: 'var(--text-primary)', lineHeight: 1.3 }}>
                                {r.anuncio_titulo || '—'}
                              </div>
                              <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '1px' }}>
                                {r.categoria_nome || '—'}
                              </div>
                            </div>
                          </div>
                        </td>

                        {/* Cliente */}
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                            <div style={{
                              width: '26px', height: '26px', borderRadius: '50%',
                              background: corAvatar(r.cliente_nome),
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              fontSize: '9px', fontWeight: '700', color: '#fff', flexShrink: 0,
                            }}>
                              {iniciais(r.cliente_nome)}
                            </div>
                            <span style={{ fontSize: '12px', color: 'var(--text-primary)', fontWeight: '500' }}>
                              {r.cliente_nome || '—'}
                            </span>
                          </div>
                        </td>

                        {/* Fornecedor */}
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                            <div style={{
                              width: '26px', height: '26px', borderRadius: '50%',
                              background: corAvatar(r.fornecedor_nome + 'x'),
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              fontSize: '9px', fontWeight: '700', color: '#fff', flexShrink: 0,
                            }}>
                              {iniciais(r.fornecedor_nome)}
                            </div>
                            <span style={{ fontSize: '12px', color: 'var(--text-primary)', fontWeight: '500' }}>
                              {r.fornecedor_nome || '—'}
                            </span>
                          </div>
                        </td>

                        {/* Período */}
                        <td>
                          <div style={{ fontSize: '11px', color: 'var(--text-secondary)', whiteSpace: 'nowrap', lineHeight: 1.5 }}>
                            <div>{fmtData(r.data_inicio)}</div>
                            <div style={{ color: 'var(--text-muted)' }}>→ {fmtData(r.data_fim)}</div>
                            {r.total_dias && (
                              <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '2px' }}>
                                {r.total_dias} dia{r.total_dias !== 1 ? 's' : ''}
                              </div>
                            )}
                          </div>
                        </td>

                        {/* Aluguer */}
                        <td>
                          <span style={{
                            fontSize: '12px', fontWeight: '700',
                            color: '#16A34A', whiteSpace: 'nowrap',
                            background: '#F0FDF4', borderRadius: '6px',
                            padding: '3px 7px', display: 'inline-block',
                          }}>
                            {fmt(r.valor_aluguer)}
                          </span>
                        </td>

                        {/* Caução */}
                        <td>
                          <span style={{
                            fontSize: '12px', fontWeight: '600',
                            color: '#D97706', whiteSpace: 'nowrap',
                            background: '#FFFBEB', borderRadius: '6px',
                            padding: '3px 7px', display: 'inline-block',
                          }}>
                            {fmt(r.valor_caucao)}
                          </span>
                        </td>

                        {/* Estado */}
                        <td>
                          <span className={`badge ${eMap.badgeCls}`} style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                            <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: eMap.dot, flexShrink: 0, display: 'inline-block' }} />
                            {eMap.label}
                          </span>
                        </td>

                        {/* Ações */}
                        <td>
                          <div style={{ display: 'flex', gap: '4px', alignItems: 'center', flexWrap: 'nowrap' }}>

                            {/* Ver detalhes */}
                            <button
                              onClick={() => setExpandido(expandido === r.id ? null : r.id)}
                              style={{
                                padding: '5px 10px', borderRadius: '20px', border: '1px solid var(--border)',
                                background: expandido === r.id ? 'var(--surface)' : 'transparent',
                                color: 'var(--text-muted)', cursor: 'pointer', fontSize: '11px',
                                display: 'flex', alignItems: 'center', gap: '4px',
                                fontFamily: 'inherit', fontWeight: '600', transition: 'all 0.15s',
                              }}
                              onMouseOver={e => { e.currentTarget.style.borderColor = 'var(--border-hover)'; e.currentTarget.style.color = 'var(--text-primary)'; }}
                              onMouseOut={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-muted)'; }}
                            >
                              <EyeIcon size={11} /> {expandido === r.id ? 'Fechar' : 'Ver'}
                            </button>

                            {/* Confirmar (pendente → confirmada) */}
                            {r.estado === 'pendente' && (
                              <button
                                onClick={() => actualizarEstado(r.id, 'confirmada', 'Confirmada')}
                                disabled={loading}
                                style={{
                                  padding: '5px 10px', borderRadius: '20px', border: 'none',
                                  background: '#DCFCE7', color: '#15803D', cursor: 'pointer', fontSize: '11px',
                                  display: 'flex', alignItems: 'center', gap: '4px',
                                  fontFamily: 'inherit', fontWeight: '700', transition: 'all 0.15s',
                                  opacity: loading ? 0.6 : 1,
                                }}
                                onMouseOver={e => { if (!loading) { e.currentTarget.style.background = '#16A34A'; e.currentTarget.style.color = '#fff'; } }}
                                onMouseOut={e => { e.currentTarget.style.background = '#DCFCE7'; e.currentTarget.style.color = '#15803D'; }}
                              >
                                <CheckIcon size={11} /> {loading ? '…' : 'Confirmar'}
                              </button>
                            )}

                            {/* Em uso (confirmada → em_uso) */}
                            {r.estado === 'confirmada' && (
                              <button
                                onClick={() => actualizarEstado(r.id, 'em_uso', 'Em uso')}
                                disabled={loading}
                                style={{
                                  padding: '5px 10px', borderRadius: '20px', border: 'none',
                                  background: '#DBEAFE', color: '#1D4ED8', cursor: 'pointer', fontSize: '11px',
                                  display: 'flex', alignItems: 'center', gap: '4px',
                                  fontFamily: 'inherit', fontWeight: '700', transition: 'all 0.15s',
                                  opacity: loading ? 0.6 : 1,
                                }}
                                onMouseOver={e => { if (!loading) { e.currentTarget.style.background = '#2563EB'; e.currentTarget.style.color = '#fff'; } }}
                                onMouseOut={e => { e.currentTarget.style.background = '#DBEAFE'; e.currentTarget.style.color = '#1D4ED8'; }}
                              >
                                <PlayIcon size={11} /> {loading ? '…' : 'Em uso'}
                              </button>
                            )}

                            {/* Concluir (em_uso → concluida) */}
                            {r.estado === 'em_uso' && (
                              <button
                                onClick={() => actualizarEstado(r.id, 'concluida', 'Concluída')}
                                disabled={loading}
                                style={{
                                  padding: '5px 10px', borderRadius: '20px', border: 'none',
                                  background: '#CCFBF1', color: '#0F766E', cursor: 'pointer', fontSize: '11px',
                                  display: 'flex', alignItems: 'center', gap: '4px',
                                  fontFamily: 'inherit', fontWeight: '700', transition: 'all 0.15s',
                                  opacity: loading ? 0.6 : 1,
                                }}
                                onMouseOver={e => { if (!loading) { e.currentTarget.style.background = '#0D9488'; e.currentTarget.style.color = '#fff'; } }}
                                onMouseOut={e => { e.currentTarget.style.background = '#CCFBF1'; e.currentTarget.style.color = '#0F766E'; }}
                              >
                                <CheckIcon size={11} /> {loading ? '…' : 'Concluir'}
                              </button>
                            )}

                            {/* Cancelar (pendente ou confirmada) */}
                            {['pendente', 'confirmada'].includes(r.estado) && (
                              <button
                                onClick={() => actualizarEstado(r.id, 'cancelada', 'Cancelada')}
                                disabled={loading}
                                title="Cancelar reserva"
                                style={{
                                  width: '26px', height: '26px', borderRadius: '50%',
                                  border: '1px solid #FECACA', background: '#FEF2F2',
                                  color: '#DC2626', cursor: 'pointer', fontSize: '11px',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  fontFamily: 'inherit', transition: 'all 0.15s', flexShrink: 0,
                                  opacity: loading ? 0.6 : 1,
                                }}
                                onMouseOver={e => { if (!loading) { e.currentTarget.style.background = '#DC2626'; e.currentTarget.style.color = '#fff'; } }}
                                onMouseOut={e => { e.currentTarget.style.background = '#FEF2F2'; e.currentTarget.style.color = '#DC2626'; }}
                              >
                                <XIcon size={11} />
                              </button>
                            )}

                            {/* Disputa */}
                            {temDisputa && (
                              <button
                                onClick={() => navigate('/admin/disputas')}
                                style={{
                                  padding: '5px 10px', borderRadius: '20px', border: 'none',
                                  background: 'linear-gradient(135deg,#FEF2F2,#FECACA)',
                                  color: '#DC2626', cursor: 'pointer', fontSize: '11px',
                                  display: 'flex', alignItems: 'center', gap: '4px',
                                  fontFamily: 'inherit', fontWeight: '700', transition: 'all 0.15s',
                                  boxShadow: '0 1px 3px rgba(220,38,38,0.2)',
                                }}
                                onMouseOver={e => { e.currentTarget.style.background = '#DC2626'; e.currentTarget.style.color = '#fff'; }}
                                onMouseOut={e => { e.currentTarget.style.background = 'linear-gradient(135deg,#FEF2F2,#FECACA)'; e.currentTarget.style.color = '#DC2626'; }}
                              >
                                <AlertIcon size={11} /> Disputa
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>

                      {/* ── Expanded detail row ── */}
                      {expandido === r.id && (
                        <tr>
                          <td colSpan={9} style={{ padding: 0 }}>
                            <div style={{
                              background: 'var(--surface)',
                              borderTop: '1px solid var(--border)',
                              borderBottom: '1px solid var(--border)',
                              padding: '20px 24px',
                            }}>
                              <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fill,minmax(150px,1fr))',
                                gap: '16px',
                                fontSize: '12px',
                              }}>
                                {[
                                  ['ID completo',   r.id],
                                  ['Produto',       r.anuncio_titulo],
                                  ['Categoria',     r.categoria_nome],
                                  ['Cliente',       r.cliente_nome],
                                  ['Fornecedor',    r.fornecedor_nome],
                                  ['Início',        fmtData(r.data_inicio)],
                                  ['Fim',           fmtData(r.data_fim)],
                                  ['Dias',          r.total_dias],
                                  ['Valor aluguer', fmt(r.valor_aluguer)],
                                  ['Caução',        fmt(r.valor_caucao)],
                                  ['Estado',        ESTADO_MAP[r.estado]?.label || r.estado],
                                  ['Criado em',     fmtData(r.created_at)],
                                ].map(([lbl, val]) => (
                                  <div key={lbl}>
                                    <div style={{
                                      fontSize: '10px', color: 'var(--text-muted)',
                                      textTransform: 'uppercase', letterSpacing: '0.5px',
                                      marginBottom: '4px', fontWeight: '600',
                                    }}>
                                      {lbl}
                                    </div>
                                    <div style={{
                                      fontWeight: '600', color: 'var(--text-primary)',
                                      wordBreak: 'break-all', fontSize: '12px',
                                    }}>
                                      {val || '—'}
                                    </div>
                                  </div>
                                ))}
                              </div>

                              {temDisputa && (
                                <div style={{
                                  marginTop: '16px', padding: '12px 16px',
                                  background: 'rgba(220,38,38,0.06)', borderRadius: '10px',
                                  border: '1px solid rgba(220,38,38,0.18)',
                                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                  gap: '12px',
                                }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#DC2626', fontSize: '12px', fontWeight: '600' }}>
                                    <AlertIcon size={14} />
                                    Disputa activa ({r.disputa_estado || 'aberta'}) — Esta reserva tem uma disputa em aberto
                                  </div>
                                  <button
                                    className="btn btn-sm btn-danger"
                                    onClick={() => navigate('/admin/disputas')}
                                    style={{ flexShrink: 0 }}
                                  >
                                    Ir para Disputas
                                  </button>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}
